import { Helmet } from "react-helmet-async";
import { useState, useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import ProblemSection from "@/components/ProblemSection";
import SolutionSection from "@/components/SolutionSection";
import TargetSection from "@/components/TargetSection";
import MetricsSection from "@/components/MetricsSection";
import OfferSection from "@/components/OfferSection";
import SocialProofSection from "@/components/SocialProofSection";
import AboutSection from "@/components/AboutSection";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";
import ScrollToTopButton from "@/components/ScrollToTopButton";

// Lazy: pulls in @supabase/* only when user opens the demo modal
const DemoModal = lazy(() => import("@/components/DemoModal"));
const LatestArticles = lazy(() => import("@/components/LatestArticles"));


const Index = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.hash]);
  const openDemo = () => setDemoOpen(true);

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <link rel="canonical" href="https://www.aquaphys.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Eaulistik",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://www.aquaphys.com",
          description: "Plateforme SaaS de monitoring intelligent des réseaux d'eau en bâtiment. Capteurs IoT (LoRa, MQTT, Modbus), détection de fuites en temps réel, prévention légionellose, conformité Décret Bacs et Décret Tertiaire. Économies mesurables : -40% eau, -30% énergie, -25% maintenance.",
          offers: [
            { "@type": "Offer", name: "Modèle PaaS — Zéro CapEx", description: "Plateforme et matériel intégrés, zéro investissement initial, financement par les économies générées. Adapté aux contrats P2 & P3.", priceCurrency: "EUR", price: "0", priceValidUntil: "2026-12-31" },
            { "@type": "Offer", name: "Modèle Achat + Service", description: "Achat du matériel IoT et abonnement SaaS. ROI inférieur à 12 mois.", priceCurrency: "EUR" }
          ],
          featureList: [
            "Surveillance IoT 24h/24 en temps réel",
            "Détection et alerte prédictive de fuites",
            "Capteurs débit, température, pression, biofilm",
            "Traitement anti-légionelles LED UVC sans chimie",
            "Réacteur hydrodynamique anti-tartre",
            "Carnet sanitaire digital automatisé",
            "Conformité Décret Bacs, Décret Tertiaire, ANSES",
            "API pour interfaçage avec GTB, GMAO, BEMS",
            "Rapports ESG et conformité intégrée"
          ],
          provider: { "@type": "Organization", name: "Aquaphys", url: "https://www.aquaphys.com" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Eaulistik est-il compatible avec mon infrastructure existante ?", acceptedAnswer: { "@type": "Answer", text: "Oui. Les capteurs IoT Eaulistik (LoRa, MQTT, Modbus) s'installent sur les réseaux existants sans travaux lourds. La solution est plug & play et interopérable via API avec vos outils de GTB (GMAO, BEMS, SI exploitants)." } },
            { "@type": "Question", name: "Combien de temps prend le déploiement d'Eaulistik ?", acceptedAnswer: { "@type": "Answer", text: "On dimensionne le besoin, on fournit les équipements, l'exploitant installe, on met en service, on forme. En cinq étapes, un réseau d'eau devient une source d'économies. Mise en service en moins de 30 jours." } },
            { "@type": "Question", name: "Comment fonctionne le modèle PaaS sans capex d'Eaulistik ?", acceptedAnswer: { "@type": "Answer", text: "Aquaphys s'appuie sur un tiers-financeur qui prend en charge l'investissement initial. Vous payez uniquement une redevance mensuelle. Aucun investissement initial — le système se finance par les économies générées. Adapté aux contrats P2 & P3." } },
            { "@type": "Question", name: "La solution Eaulistik couvre-t-elle les obligations réglementaires ?", acceptedAnswer: { "@type": "Answer", text: "Oui. Eaulistik est conçu pour répondre aux exigences du Décret Bacs, du Décret Tertiaire 2030, et aux recommandations ANSES concernant la légionellose. Carnet sanitaire digital automatisé et traçable inclus." } },
            { "@type": "Question", name: "Quels types de bâtiments Eaulistik peut-il équiper ?", acceptedAnswer: { "@type": "Answer", text: "Résidences collectives, immeubles de bureaux, hôtels, établissements de santé, écoles, universités, bâtiments publics — tout bâtiment disposant d'un réseau ECS ou de réseaux d'eau complexes. Segment prioritaire : 600 000 bâtiments publics et sociaux." } },
            { "@type": "Question", name: "Quel est le retour sur investissement d'Eaulistik ?", acceptedAnswer: { "@type": "Answer", text: "Sur une résidence de 100 logements, les économies atteignent 10 à 15 k€/an. Le ROI est inférieur à 12 mois en modèle achat, ou immédiat en modèle PaaS." } }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Eaulistik — Monitoring intelligent de l'eau en bâtiment",
          provider: { "@type": "Organization", name: "Aquaphys", url: "https://www.aquaphys.com" },
          serviceType: "Smart Water Management",
          areaServed: { "@type": "Country", name: "France" },
          availableChannel: { "@type": "ServiceChannel", serviceUrl: "https://www.aquaphys.com", servicePhone: "", availableLanguage: ["French", "English", "German", "Spanish", "Italian"] },
          description: "Service de gestion intelligente de l'eau en bâtiment combinant capteurs IoT (LoRa, MQTT, Modbus), traitement anti-légionelles UVC sans chimie, et plateforme data centralisée.",
          hasOfferCatalog: { "@type": "OfferCatalog", name: "Offres Eaulistik", itemListElement: [
            { "@type": "Offer", name: "PaaS — Zéro CapEx", description: "Zéro investissement initial. Redevance mensuelle financée par les économies générées." },
            { "@type": "Offer", name: "Achat + Service", description: "Achat matériel IoT + abonnement SaaS. ROI < 12 mois." }
          ] },
          termsOfService: "https://www.aquaphys.com/mentions-legales",
          category: ["PropTech", "WaterTech", "CleanTech", "IoT", "Smart Building"]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Comment déployer Eaulistik sur un bâtiment",
          description: "Déploiement de la solution de monitoring intelligent de l'eau Eaulistik en 5 étapes, en moins de 30 jours.",
          totalTime: "P30D",
          step: [
            { "@type": "HowToStep", position: 1, name: "Dimensionnement", text: "Analyse du réseau d'eau existant et définition des besoins en capteurs IoT et équipements de traitement." },
            { "@type": "HowToStep", position: 2, name: "Fourniture", text: "Livraison des capteurs IoT (LoRa, MQTT, Modbus), modules UVC et réacteurs hydrodynamiques." },
            { "@type": "HowToStep", position: 3, name: "Installation", text: "Mise en place par l'exploitant sur le réseau existant — installation plug & play sans travaux lourds." },
            { "@type": "HowToStep", position: 4, name: "Mise en service", text: "Configuration de la plateforme data, activation des alertes prédictives et du carnet sanitaire digital." },
            { "@type": "HowToStep", position: 5, name: "Formation", text: "Accompagnement des équipes et formation à l'utilisation du tableau de bord et des rapports." }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Eaulistik by Aquaphys — Gestion intelligente de l'eau en bâtiment",
          description: "Plateforme IoT de monitoring intelligent des réseaux d'eau.",
          url: "https://www.aquaphys.com/",
          inLanguage: ["fr-FR", "en-GB", "de-DE", "es-ES", "it-IT"],
          isPartOf: { "@type": "WebSite", name: "Eaulistik", url: "https://www.aquaphys.com" },
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "#problem h2", "#solution h2", "#faq"] }
        })}</script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navbar onDemoClick={openDemo} />
        <main>
          <HeroSection onDemoClick={openDemo} />
          <ProblemSection />
          <SolutionSection />
          <TargetSection />
          <MetricsSection />
          <OfferSection onDemoClick={openDemo} />
          <SocialProofSection />
          <Suspense fallback={null}>
            <LatestArticles title="Derniers articles" limit={3} />
          </Suspense>
          <AboutSection />
          <FAQSection />
          <CTASection onDemoClick={openDemo} />
        </main>

        <Footer />
        {demoOpen && (
          <Suspense fallback={null}>
            <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
          </Suspense>
        )}
        <ScrollToTopButton />
      </div>
    </>
  );
};

export default Index;